import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { scanShop } from "../../API/index.ts";

const UserPanel = ({ currentUser }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedId, setScannedId] = useState(null);
  const [error, setError] = useState(null);
  const [scoreCard, setScoreCard] = useState(null);
  const [winner, setWinner] = useState(null);

  const qrRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerRunning = useRef(false);

  // ✅ Preload data if shop already tagged
  useEffect(() => {
    if (currentUser?.taged) {
      console.log("🟢 Shop already tagged, skipping scan.");

      // Replace this block with a real API call if available
      const simulatedData = {
        oralSanitationScore: "N/A",
        plasticUsage: "Compliant",
        sanitationCheck: "Available",
        volunteerName: "N/A",
      };

      const simulatedWinner = {
        name: "N/A",
        score: "N/A",
        date: Date.now(),
      };

      setScoreCard(simulatedData);
      setWinner(simulatedWinner);
    }
  }, [currentUser]);

  // ✅ Initialize QR Scanner
  useEffect(() => {
    if (isScanning && qrRef.current) {
      const html5QrCode = new Html5Qrcode("user-qr-reader");
      scannerRef.current = html5QrCode;

      const qrCodeSuccessCallback = async (decodedText) => {
        if (scannerRunning.current) {
          try {
            await html5QrCode.stop();
            scannerRunning.current = false;
            setIsScanning(false);

            console.log("Decoded QR text:", decodedText);

            const match = decodedText.match(/ID:([A-Za-z0-9]+)/);
            const extractedId = match ? match[1] : null;

            if (!extractedId) {
              setError("Invalid QR Code format.");
              return;
            }

            setScannedId(extractedId);
            console.log("Extracted QR ID:", extractedId);

            try {
              console.log("Current shop ID:", currentUser?.shop?.id || currentUser?.id);
              const res = await scanShop(currentUser?.shop?.id || currentUser?.id, extractedId);
              console.log("✅ Scan API Response:", res);

              // Replace simulated data with API response if available
              const simulatedData = {
                oralSanitationScore: "N/A",
                plasticUsage: "Compliant",
                sanitationCheck: "Available",
                volunteerName: "N/A",
              };

              const simulatedWinner = {
              name: "N/A",
              score: "N/A",
              date: new Date().toISOString().slice(0, 19), // e.g., "2025-11-05T10:32:18"
              };

              setScoreCard(simulatedData);
              setWinner(simulatedWinner);
            } catch (apiErr) {
          const backendMessage =
    apiErr?.response?.data?.detail ||
    apiErr?.response?.data?.message ||
    apiErr?.message ||
    "Unknown error occurred";

  console.error("❌ Scan API failed:", backendMessage);
  setError(`Scan failed: ${backendMessage}`);
            }
          } catch (err) {
            console.warn("Stop error (ignore):", err);
          }
        }
      };

      const qrCodeErrorCallback = (err) => {
        console.debug("Scanning...", err);
      };

      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length) {
            const rearCam =
              devices.find((d) => /back|rear/i.test(d.label)) || devices[0];
            html5QrCode
              .start(
                rearCam.id,
                { fps: 10, qrbox: { width: 250, height: 250 } },
                qrCodeSuccessCallback,
                qrCodeErrorCallback
              )
              .then(() => {
                scannerRunning.current = true;
                console.log("✅ QR Scanner started");
              })
              .catch((err) => {
                console.error("Camera start failed:", err);
                setError("Camera access failed. Please refresh and allow permission.");
                setIsScanning(false);
              });
          } else {
            setError("No camera found on this device.");
            setIsScanning(false);
          }
        })
        .catch((err) => {
          console.error("Camera listing failed:", err);
          setError("Unable to access camera list.");
          setIsScanning(false);
        });
    }

    return () => {
      if (scannerRef.current && scannerRunning.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRunning.current = false;
      }
    };
  }, [isScanning]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 flex flex-col items-center">
   <picture>
  {/* Show web-banner.png on screens ≥ 768px */}
  <source
    srcSet="/assets/web-banner.png"
    media="(min-width: 768px)"
  />
  {/* Default (mobile) image */}
  <img
    src="/assets/mobile-banner.png"
    alt="Balijatra Banner"
    className="w-full max-w-sm md:max-w-3xl mb-8 rounded-lg shadow-lg"
  />
</picture>

      <h1 className="text-3xl font-bold text-indigo-700 mb-8">
        User Dashboard
      </h1>

      {/* ✅ Hide Scan Button if Shop is already tagged */}
      {!currentUser?.taged && !isScanning && !scannedId && (
        <button
          onClick={() => setIsScanning(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg shadow-md transition"
        >
          <i className="fas fa-qrcode mr-2"></i> Scan Shop QR Code
        </button>
      )}

      {isScanning && (
        <div className="flex flex-col items-center space-y-4">
          <div
            id="user-qr-reader"
            ref={qrRef}
            className="w-80 h-80 bg-gray-200 rounded-lg shadow-md"
          ></div>
          <p className="text-gray-600 text-sm">Point your camera at QR code</p>
        </div>
      )}

      {error && <p className="mt-4 text-red-600 font-medium text-center">{error}</p>}

      {scoreCard && (
        <div className="mt-10 bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl border border-green-300">
          <h2 className="text-2xl font-semibold text-green-700 mb-4">
            🌿 Your Latest Score Card
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-gray-600">Volunteer:</span>
              <span className="text-gray-800">{scoreCard.volunteerName}</span>
            </div>
            <div className="flex justify-between border-b pb-2 items-center">
              <span className="font-semibold text-gray-600">
                Sanitation Score (0-10):
              </span>
              <span className="text-5xl font-extrabold text-green-600">
                {scoreCard.oralSanitationScore}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="bg-gray-50 p-3 rounded-lg border">
                <p className="text-sm text-gray-500">Handwashing Facility:</p>
                <p className="font-bold text-base text-indigo-700">
                  {scoreCard.sanitationCheck}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border">
                <p className="text-sm text-gray-500">Plastic Usage:</p>
                <p className="font-bold text-base text-indigo-700">
                  {scoreCard.plasticUsage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {winner && (
        <div className="mt-10 bg-yellow-100 rounded-lg shadow-md border border-yellow-300 p-6 w-full max-w-2xl text-center">
          <h2 className="text-2xl font-semibold text-yellow-700 mb-3">
            🏆 Monthly Cleanliness Winner
          </h2>
          <p className="text-lg text-gray-700 font-medium">
            {winner.name} — <span className="font-bold">{winner.date}</span>
          </p>
          <p className="text-3xl font-bold text-green-700 mt-2">
            Score: {winner.score}
          </p>
        </div>
      )}
    </div>
  );
};
export default UserPanel;